import { useState } from "react";
import { Button } from "../../components/ui/button";
import { BellRing } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { waShareUrl } from "../../lib/api";
import { toast } from "sonner";
import { inr, REMINDER_TONES } from "./mockData";

export function RemindersTab({ flats }) {
  const pending = flats.filter((f) => f.status === "pending" || f.status === "overdue");
  const [tone, setTone] = useState("gentle");
  const [checked, setChecked] = useState(() => new Set(pending.map((f) => f.id)));

  const previewFlat = pending.find((f) => checked.has(f.id)) || pending[0];

  function toggle(id) {
    setChecked((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-[#F4F1EA] p-4 text-sm">
        <div className="font-semibold flex items-center gap-2"><BellRing size={16} className="text-[#C05640]" /> The app is the bad cop, not you</div>
        <p className="text-xs text-stone-600 mt-1">Reminders are worded as coming from NivasOne, not from you personally — no awkward neighbour conversations.</p>
      </div>

      <div>
        <div className="text-xs font-semibold text-stone-600 mb-1.5">Tone</div>
        <div className="flex gap-2">
          {Object.entries(REMINDER_TONES).map(([k, t]) => (
            <button key={k} onClick={() => setTone(k)} data-testid={`tone-${k}`}
              className={`px-4 py-1.5 rounded-full text-xs font-medium border transition ${tone === k ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600 hover:border-stone-500"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-stone-600 mb-1.5">{pending.length} flats pending — select who to remind</div>
        <div className="space-y-2">
          {pending.map((f) => (
            <label key={f.id} data-testid={`remind-row-${f.flat}`}
              className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white p-3 cursor-pointer hover:border-stone-400 transition">
              <input type="checkbox" checked={checked.has(f.id)} onChange={() => toggle(f.id)} className="accent-[#C05640] w-4 h-4" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{f.flat} · {f.name}</div>
                <div className="text-[11px] text-stone-500">{f.monthsPending ? `${f.monthsPending} months pending` : "Current period pending"}{f.arrears ? ` · ${inr(f.arrears)} old arrears` : ""}</div>
              </div>
              <div className="text-sm font-bold text-red-600">{inr(f.dueNow + (f.arrears || 0))}</div>
            </label>
          ))}
        </div>
      </div>

      {previewFlat && (
        <div className="rounded-2xl border border-stone-200 bg-white p-4">
          <div className="text-xs font-semibold text-stone-600 mb-2">Message preview ({previewFlat.flat})</div>
          <div className="rounded-xl rounded-tl-sm bg-[#E7FFDB] p-3 text-xs text-stone-800 leading-relaxed" data-testid="reminder-preview">
            {REMINDER_TONES[tone].text(previewFlat)}
          </div>
        </div>
      )}

      <Button data-testid="send-reminders-btn" disabled={checked.size === 0}
        onClick={() => {
          if (previewFlat) window.open(waShareUrl(REMINDER_TONES[tone].text(previewFlat)), "_blank");
          toast.success(`${checked.size} reminder(s) prepared — opens WhatsApp one by one`);
        }}
        className="w-full bg-[#25D366] hover:bg-[#1EBE55] text-white rounded-full">
        <FaWhatsapp className="mr-2" size={16} /> Send {checked.size} reminder{checked.size !== 1 ? "s" : ""} via WhatsApp
      </Button>
      <p className="text-[11px] text-stone-400 text-center -mt-2">In the real app: opens WhatsApp per resident, or auto-sends in-app + email</p>
    </div>
  );
}
